import OpenAI from 'openai';
import { BriefData } from '../types/briefTypes';

const getOpenAIClient = () => {
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  
  if (!apiKey) {
    throw new Error('OpenAI API key is missing');
  }

  return new OpenAI({
    apiKey,
    dangerouslyAllowBrowser: true
  });
};

// Export a singleton instance for other services to use
export const openai = getOpenAIClient();

interface AIPromptConfig {
  targetAudience: string;
  informationExtraction: string;
  recommendations: string;
}

let currentPrompts: AIPromptConfig = {
  targetAudience: `You are a B2B audience targeting expert. Based on the campaign context, generate a highly relevant target audience description focused on the campaign's specific topic and key messages.

CAMPAIGN CONTEXT:
Campaign Type: {{type}}
Campaign Name: {{name}}
Primary Objective: {{objective}}
Key Topic/Messages: {{keyMessages}}

Generate a B2B target audience profile that would be most interested in and impacted by this specific topic. Consider:
- Who needs to know about this topic professionally?
- Which roles are responsible for this area?
- What industries are most affected?
- What seniority levels make decisions about this?

Format the response in clear sections:

Demographics:
- Age Range: [professional age range]
- Location: [regions/countries]
- Language: [primary languages]
- Education Level: [if relevant]
- Job Titles: [specific roles most relevant to the topic]
- Seniority Level: [decision-making levels]
- Company Size: [relevant company sizes]
- Industry: [industries most affected by the topic]

Professional Interests:
- Primary Focus Areas: [topic-specific areas]
- Related Regulations/Standards: [relevant to topic]
- Professional Challenges: [related to topic]
- Current Trends: [in topic area]

Behaviors:
- Professional Platforms: [where they get information]
- Content Consumption: [what content they need]
- Professional Activities: [relevant to topic]
- Decision-Making Role: [in relation to topic]

Format as a structured, bullet-point list that could be directly used for B2B targeting on LinkedIn and other platforms.
Focus on the specific topic ({{keyMessages}}) and ensure all targeting suggestions are directly relevant to this subject matter.`,
  informationExtraction: `Jesteś asystentem AI, który wydobywa informacje o kampanii marketingowej z wiadomości użytkownika.
ZAWSZE wydobądź wszystkie faktyczne informacje z wiadomości, nawet jeśli zawiera pytanie o grupę docelową.

Przykładowe wejście: "Realizuję webinar o CSRD 25 maja. Będę promował raport. Zaproponuj grupę docelową."

Przykładowe wyjście:
{
  "campaignName": "Webinar CSRD",
  "campaignType": ["Webinar", "Raport"],
  "eventDate": "25 maja",
  "keyMessages": "CSRD",
  "primaryObjective": "Promocja Raportu",
  "eventObjective": "Generowanie Leadów",
  "channels": ["GlobalMeet", "YouTube", "LinkedIn Live", "LinkedIn Ads", "Mailing", "Landing Page"],
  "activityKPIs": [
    "Webinar KPI - Liczba Rejestracji",
    "Webinar KPI - Współczynnik Uczestnictwa",
    "Webinar KPI - Średni Czas Oglądania",
    "Webinar KPI - Liczba Pytań od Uczestników",
    "Raport KPI - Liczba Pobrań",
    "Raport KPI - Współczynnik Wypełnienia Formularza",
    "Raport KPI - Konwersja na MQL"
  ],
  "businessKPIs": [
    "Główne KPI - Liczba Leadów",
    "Główne KPI - Współczynnik Konwersji MQL>SQL",
    "Główne KPI - Wzrost Świadomości Marki",
    "Główne KPI - Engagement na LinkedIn"
  ]
}

KRYTYCZNE ZASADY:
1. ZAWSZE wydobądź wszystkie informacje, nawet jeśli wiadomość zawiera pytanie o grupę docelową.
2. MUSISZ zwrócić DOKŁADNIE ten sam format JSON z WSZYSTKIMI polami.
3. Jeśli w tekście jest słowo "raport" lub "promowali" lub "promować":
   - campaignType MUSI być ["Webinar", "Raport"]
   - MUSISZ dodać KPI dla webinaru I raportu
   - primaryObjective MUSI mieć "Promocja Raportu"
   - eventObjective MUSI mieć "Generowanie Leadów"
   - channels MUSI mieć kanały dla webinaru I raportu

4. Dla webinaru MUSISZ dodać:
   - Webinar KPIs (rejestracje, uczestnictwo, czas oglądania, pytania)
   - Kanały webinarowe (GlobalMeet, YouTube, LinkedIn Live)

5. Dla raportu MUSISZ dodać:
   - Raport KPIs (pobrania, wypełnienia formularza, konwersja)
   - Kanały raportowe (LinkedIn Ads, Mailing, Landing Page)

6. ZAWSZE dodaj businessKPIs.

PAMIĘTAJ: Odpowiedź MUSI zawierać WSZYSTKIE pola z przykładu w DOKŁADNIE takim samym formacie.`,
  recommendations: `Jesteś doświadczonym strategiem marketingowym. Na podstawie szczegółów briefu kampanii, zaproponuj 3-5 uzupełniających działań lub możliwości, które mogą zwiększyć sukces kampanii. Weź pod uwagę:

1. Dodatkowe kanały i punkty styku
2. Działania przed i po wydarzeniu
3. Możliwości content marketingowe
4. Strategie angażowania odbiorców
5. Możliwości nurturowania leadów
6. Taktyki pomiaru i follow-up

Formatuj każdą sugestię jako element akcji zaczynający się od czasownika (np. "Stwórz", "Uruchom", "Opracuj").
Dostosuj sugestie do konkretnego kontekstu kampanii.`
};

export const updateAIPrompts = (newPrompts: AIPromptConfig) => {
  currentPrompts = { ...currentPrompts, ...newPrompts };
};

export const extractInformation = async (input: string) => {
  try {
    console.log('Processing input:', input);
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [
        {
          role: "system",
          content: currentPrompts.informationExtraction
        },
        {
          role: "user",
          content: input
        }
      ],
      temperature: 0.1,
    });

    const result = response.choices[0]?.message?.content;
    if (!result) {
      console.error('No content in response');
      return {};
    }

    try {
      console.log('Raw result:', result);
      const parsedResult = JSON.parse(result);
      console.log('Parsed result:', parsedResult);

      // Create a properly formatted result with what we have
      const formattedResult: Partial<BriefData> = {
        campaignName: parsedResult.campaignName,
        campaignType: Array.isArray(parsedResult.campaignType) 
          ? parsedResult.campaignType.join(' i ') 
          : parsedResult.campaignType,
        eventDate: parsedResult.eventDate,
        keyMessages: Array.isArray(parsedResult.keyMessages) 
          ? parsedResult.keyMessages.join('\n') 
          : parsedResult.keyMessages,
        primaryObjective: parsedResult.primaryObjective || parsedResult.objective,
        eventObjective: parsedResult.eventObjective,
        channels: Array.isArray(parsedResult.channels)
          ? parsedResult.channels.join(', ')
          : parsedResult.channels,
        activityKPIs: Array.isArray(parsedResult.activityKPIs)
          ? parsedResult.activityKPIs.join('\n')
          : parsedResult.activityKPIs,
        businessKPIs: Array.isArray(parsedResult.businessKPIs)
          ? parsedResult.businessKPIs.join('\n')
          : parsedResult.businessKPIs
      };

      // If we have a webinar or report mention, ensure proper campaign type and KPIs
      const hasWebinar = formattedResult.campaignType?.toLowerCase().includes('webinar') || 
                        input.toLowerCase().includes('webinar');
      const hasReport = input.toLowerCase().includes('raport') || 
                       input.toLowerCase().includes('promowa');

      if (hasWebinar || hasReport) {
        // Set campaign type
        formattedResult.campaignType = hasReport ? 'Webinar i Raport' : 'Webinar';

        // Set default KPIs if not provided
        if (!formattedResult.activityKPIs) {
          const defaultActivityKPIs = [
            ...(hasWebinar ? [
              "Webinar KPI:",
              "- Liczba Rejestracji",
              "- Współczynnik Uczestnictwa",
              "- Średni Czas Oglądania",
              "- Liczba Pytań od Uczestników"
            ] : []),
            ...(hasReport ? [
              "Raport KPI:",
              "- Liczba Pobrań",
              "- Współczynnik Wypełnienia Formularza",
              "- Konwersja na MQL"
            ] : [])
          ];
          formattedResult.activityKPIs = defaultActivityKPIs.join('\n');
        }

        if (!formattedResult.businessKPIs) {
          const defaultBusinessKPIs = [
            "Główne KPI:",
            "- Liczba Leadów",
            "- Współczynnik Konwersji MQL>SQL",
            "- Wzrost Świadomości Marki",
            "- Engagement na LinkedIn"
          ];
          formattedResult.businessKPIs = defaultBusinessKPIs.join('\n');
        }
      }

      // Log what we're returning
      console.log('Returning formatted result:', formattedResult);
      return formattedResult;
    } catch (error) {
      console.error('Error processing response:', error);
      console.error('Failed to parse:', result);
      return {};
    }
  } catch (error) {
    console.error('Error in extractInformation:', error);
    throw error;
  }
};

export async function generateRecommendations(briefData: BriefData): Promise<string[]> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [
        { role: "system", content: currentPrompts.recommendations },
        { role: "user", content: JSON.stringify({
          type: briefData.campaignType,
          primaryObjective: briefData.primaryObjective,
          eventObjective: briefData.eventObjective,
          audience: briefData.targetAudience,
          keyMessages: briefData.keyMessages,
          channels: briefData.channels
        })}
      ],
      temperature: 0.8,
    });

    const suggestions = response.choices[0]?.message?.content?.split('\n')
      .filter(line => line.trim())
      .map(line => line.replace(/^\d+\.\s*/, '').trim()) || [];
    
    return suggestions;
  } catch (error) {
    console.error('Error generating recommendations:', error);
    return [];
  }
}

export async function generateTargetAudience(briefData: BriefData): Promise<string> {
  try {
    console.log('Generating target audience for brief:', briefData);
    
    // Create a more detailed context about the campaign and its topic
    const campaignContext = `
Campaign Type: ${briefData.campaignType || 'Nie określono'}
Campaign Name: ${briefData.campaignName || 'Nie określono'}
Primary Objective: ${briefData.primaryObjective || 'Nie określono'}
Key Topic/Messages: ${briefData.keyMessages || 'Nie określono'}

Additional Context:
${briefData.keyMessages ? `This campaign is focused on ${briefData.keyMessages}. ` : ''}
${briefData.keyMessages === 'CSRD' ? 
  `The Corporate Sustainability Reporting Directive (CSRD) is a European Union law that requires large companies to report on their environmental and social impact. This includes reporting on sustainability matters, environmental protection, social responsibility, treatment of employees, respect for human rights, anti-corruption, and diversity on company boards.` : 
  ''}
${briefData.keyMessages === 'ESG' ? 
  `ESG (Environmental, Social, and Governance) reporting and compliance is a framework used to measure the sustainability and societal impact of companies and investments.` : 
  ''}`;

    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [
        { 
          role: "system", 
          content: currentPrompts.targetAudience 
        },
        { 
          role: "user", 
          content: campaignContext
        },
        {
          role: "user",
          content: "Based on this specific campaign context and topic, generate detailed targeting suggestions that would reach the most relevant B2B audience for this subject matter."
        }
      ],
      temperature: 0.7,
    });

    const suggestion = response.choices[0]?.message?.content;
    console.log('Generated target audience suggestion:', suggestion);

    if (!suggestion) {
      console.warn('No suggestion generated');
      return 'Przepraszam, nie udało mi się wygenerować sugestii grup docelowych. Spróbuj ponownie.';
    }

    return `Oto propozycje targetowania dla różnych platform reklamowych:\n\n${suggestion}`;
  } catch (error) {
    console.error('Error generating target audience:', error);
    return 'Przepraszam, wystąpił błąd podczas generowania grup docelowych. Spróbuj ponownie.';
  }
} 